const normalsGLSL = /* glsl */ `

/*
 * ============================================================
 * NORMAL / HEIGHT SURFACE DATA
 * ============================================================
 */

uniform sampler2D uNormalMap;
uniform bool uHasNormalMap;
uniform float uNormalStrength;

uniform sampler2D uHeightMap;
uniform bool uHasHeightMap;
uniform float uHeightStrength;


/*
 * ============================================================
 * TANGENT BASIS
 * ============================================================
 *
 * Construct the tangent basis from
 * screen-space derivatives rather than
 * requiring tangent vertex attributes.
 */
mat3 buildTBN(
    vec3 normal,
    vec2 uv
) {

    vec3 positionDx =
        dFdx(
            vWorldPosition
        );

    vec3 positionDy =
        dFdy(
            vWorldPosition
        );


    vec2 uvDx =
        dFdx(
            uv
        );

    vec2 uvDy =
        dFdy(
            uv
        );


    float determinant =
        uvDx.x *
        uvDy.y -
        uvDx.y *
        uvDy.x;


    /*
     * Degenerate UV fallback.
     */
    if (
        abs(
            determinant
        ) <
        0.000001
    ) {

        vec3 helper =
            abs(
                normal.z
            ) <
            0.999

                ? vec3(
                    0.0,
                    0.0,
                    1.0
                )

                : vec3(
                    0.0,
                    1.0,
                    0.0
                );


        vec3 tangent =
            normalize(
                cross(
                    helper,
                    normal
                )
            );


        vec3 bitangent =
            normalize(
                cross(
                    normal,
                    tangent
                )
            );


        return mat3(
            tangent,
            bitangent,
            normal
        );
    }


    float inverseDeterminant =
        1.0 /
        determinant;


    vec3 tangent =
        (
            positionDx *
            uvDy.y -
            positionDy *
            uvDx.y
        ) *
        inverseDeterminant;


    tangent =
        normalize(
            tangent -
            normal *
            dot(
                normal,
                tangent
            )
        );


    float handedness =
        determinant <
        0.0
            ? -1.0
            : 1.0;


    vec3 bitangent =
        normalize(
            cross(
                normal,
                tangent
            )
        ) *
        handedness;


    return mat3(
        tangent,
        bitangent,
        normal
    );
}


/*
 * ============================================================
 * NORMAL MAP
 * ============================================================
 */

vec3 applyNormalMap(
    vec3 normal,
    vec2 uv,
    mat3 tbn
) {

    if (
        !uHasNormalMap
    ) {

        return normal;
    }


    vec3 mappedNormal =
        texture2D(
            uNormalMap,
            uv
        ).xyz;


    mappedNormal =
        mappedNormal *
        2.0 -
        1.0;


    mappedNormal.xy *=
        uNormalStrength;


    mappedNormal =
        normalize(
            mappedNormal
        );


    return normalize(
        tbn *
        mappedNormal
    );
}


/*
 * ============================================================
 * HEIGHT / BUMP MAP
 * ============================================================
 */

vec3 applyHeightMap(
    vec3 normal,
    vec2 uv,
    mat3 tbn
) {

    if (
        !uHasHeightMap
    ) {

        return normal;
    }


    vec2 uvDx =
        dFdx(
            uv
        );

    vec2 uvDy =
        dFdy(
            uv
        );


    float centerHeight =
        texture2D(
            uHeightMap,
            uv
        ).r;


    float heightX =
        texture2D(
            uHeightMap,
            uv + uvDx
        ).r;


    float heightY =
        texture2D(
            uHeightMap,
            uv + uvDy
        ).r;


    float gradientX =
        heightX -
        centerHeight;


    float gradientY =
        heightY -
        centerHeight;


    vec3 bumpNormal =
        normalize(
            vec3(
                -gradientX *
                uHeightStrength *
                100.0,

                -gradientY *
                uHeightStrength *
                100.0,

                1.0
            )
        );


    vec3 worldBumpNormal =
        normalize(
            tbn *
            bumpNormal
        );


    return normalize(
        normal +
        (
            worldBumpNormal -
            tbn[2]
        )
    );
}


/*
 * Convenience function used by
 * every current material.
 */
vec3 getSurfaceNormal(
    vec2 uv
) {

    vec3 geometricNormal =
        normalize(
            vNormal
        );


    if (
        !gl_FrontFacing
    ) {

        geometricNormal =
            -geometricNormal;
    }


    mat3 tbn =
        buildTBN(
            geometricNormal,
            uv
        );


    vec3 surfaceNormal =
        applyNormalMap(
            geometricNormal,
            uv,
            tbn
        );


    surfaceNormal =
        applyHeightMap(
            surfaceNormal,
            uv,
            tbn
        );


    return surfaceNormal;
}

`;

export default normalsGLSL;
